/**
 * CreateProjectModal - Modal for creating a new project with GitHub and Jira integration
 */

import { X, Check, AlertCircle } from 'lucide-react';
import type { Repository, JiraProject } from '../../lib/integrations-api';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: () => void;
  form: {
    name: string;
    description: string;
    selectedRepos: Repository[];
    selectedJiraProject: JiraProject | null;
  };
  onFormChange: (form: {
    name: string;
    description: string;
    selectedRepos: Repository[];
    selectedJiraProject: JiraProject | null;
  }) => void;
  repositories: Repository[];
  jiraProjects: JiraProject[];
  isFormValid: boolean;
  isSaving: boolean;
}

export function CreateProjectModal ({
  isOpen,
  onClose,
  onCreate,
  form,
  onFormChange,
  repositories,
  jiraProjects,
  isFormValid,
  isSaving,
}: CreateProjectModalProps) {
  if (!isOpen) return null;

  const handleRepoToggle = (repo: Repository, checked: boolean) => {
    if (checked) {
      onFormChange({
        ...form,
        selectedRepos: [...form.selectedRepos, repo],
      });
    } else {
      onFormChange({
        ...form,
        selectedRepos: form.selectedRepos.filter((r) => r.id !== repo.id),
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New Project
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => onFormChange({ ...form, name: e.target.value })}
              placeholder="Enter project name"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
              placeholder="Describe your project"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
            />
          </div>

          {/* GitHub Integration */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              GitHub Integration
            </h4>

            {/* Connection Status */}
            <div className="flex items-center justify-between mb-4 p-3 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-300">
                    GitHub Connected
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    {repositories.length} repositories available
                  </p>
                </div>
              </div>
            </div>

            {/* Repository Selection */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Select Repositories ({form.selectedRepos.length} selected)
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                {repositories.map((repo) => (
                  <label
                    key={repo.id}
                    className="flex items-start gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.selectedRepos.some((r) => r.id === repo.id)}
                      onChange={(e) => handleRepoToggle(repo, e.target.checked)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white text-sm">
                          {repo.full_name}
                        </span>
                        <span
                          className={`px-2 py-0.5 text-xs rounded-xl ${repo.private
                              ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400'
                              : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                            }`}
                        >
                          {repo.private ? 'Private' : 'Public'}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
                {repositories.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No repositories found
                  </p>
                )}
              </div>
              {form.selectedRepos.length === 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Please select at least one repository
                </p>
              )}
            </div>
          </div>

          {/* Jira Integration */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Jira Integration
            </h4>

            {/* Connection Status */}
            <div className="flex items-center justify-between mb-4 p-3 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-300">
                    Jira Connected
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    {jiraProjects.length} projects available
                  </p>
                </div>
              </div>
            </div>

            {/* Jira Project Selection */}
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Select Jira Project
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                {jiraProjects.map((project) => (
                  <label
                    key={project.id}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="jiraProject"
                      checked={form.selectedJiraProject?.id === project.id}
                      onChange={() =>
                        onFormChange({ ...form, selectedJiraProject: project })
                      }
                    />
                    <div className="flex-1 flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">
                        {project.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400 text-xs rounded-xl font-mono">
                          {project.key}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-900/20 text-gray-800 dark:text-gray-400 text-xs rounded-xl">
                          {project.projectTypeKey}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
                {jiraProjects.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No Jira projects found
                  </p>
                )}
              </div>
              {!form.selectedJiraProject && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Please select a Jira project
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onCreate}
            disabled={!isFormValid || isSaving}
            className={`px-4 py-2 rounded-xl font-medium transition-all ${isFormValid && !isSaving
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              }`}
          >
            {isSaving ? 'Creating Project...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
}
